import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import { Skeleton, StatsGridSkeleton } from '../common/Skeleton';

export default function HROverview() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalRequisitions: 0,
    openRequisitions: 0,
    totalScreened: 0,
    totalScheduled: 0,
    totalExits: 0,
    pendingExits: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOverviewStats = async () => {
      try {
        setLoading(true);

        const [reqRes, exitsRes, candRes, roomsRes, globalReqRes] = await Promise.allSettled([
          api.get('/api/requisitions/overview-stats'),
          api.get('/api/employees/exits/all'),
          api.get('/api/screening/candidates/all'),
          api.get('/api/auth/interview-rooms'),
          api.get('/api/requisitions/global')
        ]);

        const reqData = reqRes.status === 'fulfilled' ? reqRes.value.data : null;
        const exitsData = exitsRes.status === 'fulfilled' && Array.isArray(exitsRes.value.data) ? exitsRes.value.data : [];
        const candData = candRes.status === 'fulfilled' && Array.isArray(candRes.value.data) ? candRes.value.data : [];
        const roomsData = roomsRes.status === 'fulfilled' && Array.isArray(roomsRes.value.data) ? roomsRes.value.data : [];
        const globalReqData = globalReqRes.status === 'fulfilled' && Array.isArray(globalReqRes.value.data) ? globalReqRes.value.data : [];

        // Determine requisitions count from overview-stats or directly from global list
        const totalReqs = Number(reqData?.totalRequisitions !== undefined ? reqData.totalRequisitions : globalReqData.length) || 0;
        const openReqs = Number(reqData?.openRequisitions !== undefined ? reqData.openRequisitions : globalReqData.filter(r => ['PUBLISHED', 'OPEN', 'BU_APPROVED'].includes(r.status)).length) || 0;

        setStats({
          totalRequisitions: totalReqs,
          openRequisitions: openReqs,
          totalScreened: candData.length || 0,
          totalScheduled: roomsData.length || 0,
          totalExits: exitsData.length || 0,
          pendingExits: exitsData.filter(e => e.exit_status === 'EXIT_PENDING').length || 0
        });
      } catch (err) {
        console.warn('⚠️ Failed to load overview stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOverviewStats();
  }, []);

  // Compute funnel metrics
  const screenedCount = stats.totalScreened;
  const scheduledCount = stats.totalScheduled;
  const evaluatedCount = Math.round(stats.totalScheduled * 0.45); // simulated cleared ratio
  const hiredCount = Math.round(stats.totalScreened * 0.08); // simulated hired ratio

  if (loading) {
    return (
      <div style={styles.container}>
        <header style={styles.header}>
          <h2 style={styles.title}>Welcome back, HR Admin</h2>
          <p style={styles.subtitle}>Here is a high-level summary of your hiring funnel, active requisitions, and exit clearances.</p>
        </header>

        {/* Shimmer Stats Cards */}
        <StatsGridSkeleton count={4} />

        {/* Shimmer Two Columns */}
        <div style={styles.contentGrid}>
          <div style={styles.chartCard}>
            <Skeleton width="180px" height="20px" borderRadius="6px" style={{ marginBottom: '8px' }} />
            <Skeleton width="280px" height="13px" borderRadius="4px" style={{ marginBottom: '24px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <Skeleton width="100%" height="28px" borderRadius="8px" />
              <Skeleton width="100%" height="28px" borderRadius="8px" />
              <Skeleton width="100%" height="28px" borderRadius="8px" />
              <Skeleton width="100%" height="28px" borderRadius="8px" />
            </div>
          </div>

          <div style={styles.actionsCard}>
            <Skeleton width="140px" height="20px" borderRadius="6px" style={{ marginBottom: '8px' }} />
            <Skeleton width="240px" height="13px" borderRadius="4px" style={{ marginBottom: '20px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Skeleton width="100%" height="52px" borderRadius="12px" />
              <Skeleton width="100%" height="52px" borderRadius="12px" />
              <Skeleton width="100%" height="52px" borderRadius="12px" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h2 style={styles.title}>Welcome back, HR Admin</h2>
        <p style={styles.subtitle}>Here is a high-level summary of your hiring funnel, active requisitions, and exit clearances.</p>
      </header>

      {/* Grid Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statIconWrapper}>💼</div>
          <div style={styles.statInfo}>
            <span style={styles.statLabel}>Active Requisitions</span>
            <span style={styles.statValue}>{stats.openRequisitions} / {stats.totalRequisitions}</span>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={{ ...styles.statIconWrapper, backgroundColor: '#dbeafe', color: '#2563eb' }}>📄</div>
          <div style={styles.statInfo}>
            <span style={styles.statLabel}>Screened Candidates</span>
            <span style={styles.statValue}>{stats.totalScreened}</span>
          </div>
        </div>

        <div style={{ ...styles.statCard }}>
          <div style={{ ...styles.statIconWrapper, backgroundColor: '#fef3c7', color: '#d97706' }}>📅</div>
          <div style={styles.statInfo}>
            <span style={styles.statLabel}>Interviews Scheduled</span>
            <span style={styles.statValue}>{stats.totalScheduled}</span>
          </div>
        </div>

        <div style={{ ...styles.statCard }}>
          <div style={{ ...styles.statIconWrapper, backgroundColor: '#fee2e2', color: '#dc2626' }}>🚪</div>
          <div style={styles.statInfo}>
            <span style={styles.statLabel}>Pending Exit Clearances</span>
            <span style={styles.statValue}>{stats.pendingExits}</span>
          </div>
        </div>
      </div>

      {/* Two Columns: Pipeline Chart & Quick Actions */}
      <div style={styles.contentGrid}>
        {/* Pipeline Summary Chart */}
        <div style={styles.chartCard}>
          <h3 style={styles.sectionTitle}>Hiring Funnel Status</h3>
          <p style={styles.sectionSubtitle}>Visual progress of candidates across hiring stages.</p>
          
          <div style={styles.chartContainer}>
            <div style={styles.chartBarRow}>
              <span style={styles.chartBarLabel}>Screened</span>
              <div style={styles.barTrack}>
                <div style={{ ...styles.barFill, width: '100%', backgroundColor: '#3b82f6' }} />
              </div>
              <span style={styles.chartBarCount}>{screenedCount}</span>
            </div>

            <div style={styles.chartBarRow}>
              <span style={styles.chartBarLabel}>Scheduled</span>
              <div style={styles.barTrack}>
                <div style={{ ...styles.barFill, width: screenedCount ? `${Math.min((scheduledCount/screenedCount)*100, 100)}%` : '0%', backgroundColor: '#f59e0b' }} />
              </div>
              <span style={styles.chartBarCount}>{scheduledCount}</span>
            </div>

            <div style={styles.chartBarRow}>
              <span style={styles.chartBarLabel}>Evaluated</span>
              <div style={styles.barTrack}>
                <div style={{ ...styles.barFill, width: screenedCount ? `${Math.min((evaluatedCount/screenedCount)*100, 100)}%` : '0%', backgroundColor: '#10b981' }} />
              </div>
              <span style={styles.chartBarCount}>{evaluatedCount}</span>
            </div>

            <div style={styles.chartBarRow}>
              <span style={styles.chartBarLabel}>Hired</span>
              <div style={styles.barTrack}>
                <div style={{ ...styles.barFill, width: screenedCount ? `${Math.min((hiredCount/screenedCount)*100, 100)}%` : '0%', backgroundColor: '#8b5cf6' }} />
              </div>
              <span style={styles.chartBarCount}>{hiredCount}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions Card */}
        <div style={styles.actionsCard}>
          <h3 style={styles.sectionTitle}>Quick Workflows</h3>
          <p style={styles.sectionSubtitle}>Jump straight to active HR tasks.</p>
          
          <div style={styles.actionsList}>
            <button onClick={() => navigate('/hr-dashboard/jd-builder')} style={styles.actionBtn}>
              <span style={styles.actionIcon}>➕</span>
              <div style={styles.actionTextWrapper}>
                <span style={styles.actionTitle}>Create Requisition</span>
                <span style={styles.actionDesc}>Build new JDs and publish open positions.</span>
              </div>
            </button>

            <button onClick={() => navigate('/hr-dashboard/screening')} style={styles.actionBtn}>
              <span style={styles.actionIcon}>📤</span>
              <div style={styles.actionTextWrapper}>
                <span style={styles.actionTitle}>Upload Resumes</span>
                <span style={styles.actionDesc}>Bulk parse and scan candidate resumes with Gemini.</span>
              </div>
            </button>

            <button onClick={() => navigate('/hr-dashboard/exits')} style={styles.actionBtn}>
              <span style={styles.actionIcon}>📋</span>
              <div style={styles.actionTextWrapper}>
                <span style={styles.actionTitle}>Manage Clearances</span>
                <span style={styles.actionDesc}>Verify exit checklist items and offboard members.</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    textAlign: 'left'
  },
  header: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '800',
    color: '#0f172a',
    letterSpacing: '-0.02em'
  },
  subtitle: {
    margin: '6px 0 0 0',
    fontSize: '14.5px',
    color: '#64748b',
    lineHeight: '1.5'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px'
  },
  statCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
  },
  statIconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: '#dcfce7',
    color: '#15803d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 'bold',
    flexShrink: 0
  },
  statInfo: {
    display: 'flex',
    flexDirection: 'column'
  },
  statLabel: {
    fontSize: '12.5px',
    fontWeight: '600',
    color: '#64748b'
  },
  statValue: {
    fontSize: '20px',
    fontWeight: '800',
    color: '#0f172a',
    marginTop: '4px'
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '24px'
  },
  chartCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column'
  },
  actionsCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column'
  },
  sectionTitle: {
    margin: 0,
    fontSize: '17px',
    fontWeight: '700',
    color: '#0f172a'
  },
  sectionSubtitle: {
    margin: '4px 0 20px 0',
    fontSize: '13px',
    color: '#64748b'
  },
  chartContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: 1,
    justifyContent: 'center'
  },
  chartBarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  chartBarLabel: {
    width: '80px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    textAlign: 'right'
  },
  barTrack: {
    flex: 1,
    height: '12px',
    backgroundColor: '#f1f5f9',
    borderRadius: '99px',
    overflow: 'hidden'
  },
  barFill: {
    height: '100%',
    borderRadius: '99px',
    transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  chartBarCount: {
    width: '30px',
    fontSize: '13px',
    fontWeight: '700',
    color: '#0f172a',
    textAlign: 'left'
  },
  actionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flex: 1,
    justifyContent: 'center'
  },
  actionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    borderStyle: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    textAlign: 'left'
  },
  actionIcon: {
    fontSize: '24px',
    flexShrink: 0
  },
  actionTextWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  actionTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#0f172a'
  },
  actionDesc: {
    fontSize: '11.5px',
    color: '#64748b',
    lineHeight: '1.4'
  }
};
